import defaultSettings from '../settings.json';

// Tab 持久化存储键
const TAB_STORAGE_KEY = 'tobiz_account_tabs';
const ACTIVE_TAB_STORAGE_KEY = 'tobiz_account_active_tab';
const VISITED_TABS_STORAGE_KEY = 'tobiz_account_visited_tabs';

// 从 localStorage 读取 tab 状态
function loadTabState() {
  try {
    const tabs = localStorage.getItem(TAB_STORAGE_KEY);
    const activeTab = localStorage.getItem(ACTIVE_TAB_STORAGE_KEY);
    const visitedTabs = localStorage.getItem(VISITED_TABS_STORAGE_KEY);
    return {
      tabs: tabs ? JSON.parse(tabs) : [],
      activeTab: activeTab || '',
      visitedTabs: visitedTabs ? JSON.parse(visitedTabs) : []
    };
  } catch (e) {
    console.warn('Failed to load tab state from localStorage:', e);
    return {
      tabs: [],
      activeTab: '',
      visitedTabs: []
    };
  }
}

// 保存 tab 状态到 localStorage
function saveTabState(state) {
  try {
    localStorage.setItem(TAB_STORAGE_KEY, JSON.stringify(state.tabs || []));
    localStorage.setItem(ACTIVE_TAB_STORAGE_KEY, state.activeTab || '');
    localStorage.setItem(VISITED_TABS_STORAGE_KEY, JSON.stringify(state.visitedTabs || []));
  } catch (e) {
    console.warn('Failed to save tab state to localStorage:', e);
  }
}
// 从 localStorage 恢复 tab 状态，实现刷新后 tab 持久化
const persistedTabState = loadTabState();
const initialState = {
  settings: defaultSettings,
  userInfo: {
    permissions: {}
  },
  companies: [],
  currentCompany: null,
  tabs: persistedTabState.tabs || [],
  activeTab: persistedTabState.activeTab || '',
  visitedTabs: persistedTabState.visitedTabs || []
};

// LRU 缓存上限：最多同时挂载 8 个页面组件
const MAX_CACHED_TABS = 8;

/**
 * 更新 visitedTabs（LRU 顺序）
 * - 如果 key 已存在，移到末尾（最近访问）
 * - 如果不存在，添加到末尾
 * - 如果超过上限，移除最久未访问的（最前面的）
 */
function updateVisitedTabsLRU(visitedTabs, key) {
  const filtered = visitedTabs.filter(k => k !== key);
  const updated = [...filtered, key];
  // 超过上限时，淘汰最久未访问的
  if (updated.length > MAX_CACHED_TABS) {
    return updated.slice(updated.length - MAX_CACHED_TABS);
  }
  return updated;
}
export default function store(state = initialState, action) {
  switch (action.type) {
    case 'update-settings':
      {
        const {
          settings
        } = action.payload;
        return {
          ...state,
          settings
        };
      }
    case 'update-userInfo':
      {
        const {
          userInfo = initialState.userInfo,
          userLoading
        } = action.payload;
        return {
          ...state,
          userLoading,
          userInfo
        };
      }
    case 'update-companies':
      {
        const {
          companies
        } = action.payload;
        return {
          ...state,
          companies
        };
      }
    case 'update-currentCompany':
      {
        const {
          currentCompany
        } = action.payload;
        return {
          ...state,
          currentCompany
        };
      }
    case 'add-tab':
      {
        const {
          tab
        } = action.payload;
        const existingTab = state.tabs?.find(t => t.key === tab.key);
        const newVisitedTabs = updateVisitedTabsLRU(state.visitedTabs || [], tab.key);
        if (existingTab) {
          const newState = {
            ...state,
            activeTab: tab.key,
            visitedTabs: newVisitedTabs
          };
          saveTabState(newState);
          return newState;
        }
        const newState = {
          ...state,
          tabs: [...(state.tabs || []), {
            ...tab,
            closable: tab.closable !== false
          }],
          activeTab: tab.key,
          visitedTabs: newVisitedTabs
        };
        saveTabState(newState);
        return newState;
      }
    case 'remove-tab':
      {
        const {
          key
        } = action.payload;
        const newTabs = state.tabs?.filter(t => t.key !== key) || [];
        const newActiveTab = state.activeTab === key ? newTabs[newTabs.length - 1]?.key || '' : state.activeTab;
        const newVisitedTabs = (state.visitedTabs || []).filter(k => k !== key);
        const newState = {
          ...state,
          tabs: newTabs,
          activeTab: newActiveTab,
          visitedTabs: newVisitedTabs
        };
        saveTabState(newState);
        return newState;
      }
    case 'set-active-tab':
      {
        const {
          key
        } = action.payload;
        const newState = {
          ...state,
          activeTab: key,
          visitedTabs: updateVisitedTabsLRU(state.visitedTabs || [], key)
        };
        saveTabState(newState);
        return newState;
      }
    case 'close-other-tabs':
      {
        const {
          key
        } = action.payload;
        const keptTab = state.tabs?.find(t => t.key === key);
        const newState = {
          ...state,
          tabs: keptTab ? [keptTab] : [],
          activeTab: key,
          visitedTabs: keptTab ? [key] : []
        };
        saveTabState(newState);
        return newState;
      }
    case 'close-all-tabs':
      {
        const newState = {
          ...state,
          tabs: [],
          activeTab: '',
          visitedTabs: []
        };
        saveTabState(newState);
        return newState;
      }
    case 'refresh-tab':
      {
        const {
          key
        } = action.payload;
        const newTabs = state.tabs?.map(tab => {
          if (tab.key === key) {
            return {
              ...tab,
              refreshKey: (tab.refreshKey || 0) + 1
            };
          }
          return tab;
        });
        const newState = {
          ...state,
          tabs: newTabs
        };
        saveTabState(newState);
        return newState;
      }
    case 'refresh-all-tabs':
      {
        const newTabs = state.tabs?.map(tab => ({
          ...tab,
          refreshKey: (tab.refreshKey || 0) + 1
        }));
        const newState = {
          ...state,
          tabs: newTabs
        };
        saveTabState(newState);
        return newState;
      }
    case 'toggle-pin-tab':
      {
        const {
          key
        } = action.payload;
        const newTabs = state.tabs?.map(tab => {
          if (tab.key === key) {
            return {
              ...tab,
              pinned: !tab.pinned,
              closable: tab.pinned
            };
          }
          return tab;
        });
        const newState = {
          ...state,
          tabs: newTabs
        };
        saveTabState(newState);
        return newState;
      }
    case 'reorder-tabs':
      {
        const {
          fromIndex,
          toIndex
        } = action.payload;
        const newTabs = [...(state.tabs || [])];
        const [movedTab] = newTabs.splice(fromIndex, 1);
        newTabs.splice(toIndex, 0, movedTab);
        const newState = {
          ...state,
          tabs: newTabs
        };
        saveTabState(newState);
        return newState;
      }
    default:
      return state;
  }
}